// frontend/src/pages/admin/PackageEditPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Loader2, Globe, Save, Plus, Trash2, X,
  Bold, Italic, List, ListOrdered, Link as LinkIcon,
  Image as ImageIcon, Code, AlignLeft, AlignCenter, AlignRight,
  Heading1, Heading2, Heading3, Undo, Redo
} from 'lucide-react';
import { SEOEditor } from '../../components/SEO/SEOEditor';
import { PageSEO } from '../../components/SEO/PageSEO';
import config from '../../config';

// ============================================================
// تابع تبدیل متن به slug
// ============================================================
const slugify = (text) => {
  if (!text) return '';

  let slug = text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
    .replace(/[^\w\u0600-\u06FF\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (!slug) {
    slug = 'package-' + Date.now();
  }
  return slug;
};

// ============================================================
// تابع پردازش تصاویر (نمایش در ادیتور)
// ============================================================
const processImageUrls = (html) => {
  if (!html) return '';

  let processed = html;

  // تبدیل همه انواع مسیرهای تصویر به URL کامل
  const patterns = [
    /src="\/media\/([^"]+)"/g,
    /src="media\/([^"]+)"/g,
    /src="https?:\/\/[^/]+\/media\/([^"]+)"/g,
  ];

  patterns.forEach(pattern => {
    processed = processed.replace(pattern, (match, path) => {
      const fullUrl = config.getMediaUrl(`/media/${path}`);
      return `src="${fullUrl}"`;
    });
  });

  return processed;
};

// ============================================================
// تابع پاک‌سازی مسیر تصاویر برای ذخیره در دیتابیس
// ============================================================
const cleanImageUrls = (html) => {
  if (!html) return '';

  return html.replace(
    /src="(?:https?:\/\/[^\/]+)?\/media\/([^"]+)"/g,
    (match, path) => {
      return `src="/media/${path}"`;
    }
  );
};

// ============================================================
// نوار ابزار ویرایشگر
// ============================================================
const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);

  const handleImageUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageUploadError('لطفاً فقط فایل تصویر انتخاب کنید');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setImageUploadError('حجم تصویر نباید بیشتر از 2 مگابایت باشد');
      return;
    }

    setUploadingImage(true);
    setImageUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/products/packages/upload-image/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در آپلود تصویر');
      }

      const data = await response.json();

      if (editor && data.image_url) {
        // استخراج مسیر نسبی از URL کامل
        const relativePath = data.image_url.replace(/^https?:\/\/[^\/]+/, '');
        editor.chain().focus().setImage({ src: relativePath }).run();
        toast.success('تصویر با موفقیت آپلود شد');
      } else {
        toast.error('خطا در دریافت مسیر تصویر');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageUploadError(error.message || 'خطا در آپلود تصویر');
      toast.error(error.message || 'خطا در آپلود تصویر');
    } finally {
      setUploadingImage(false);
    }
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleImageUpload(file);
      }
    };
    input.click();
  };

  const addLink = () => {
    const url = window.prompt('لینک را وارد کنید:', 'https://');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="bg-gray-50 border-b p-2 flex flex-wrap gap-1 sticky top-0 z-10 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="p-1.5 rounded hover:bg-gray-200 transition disabled:opacity-50"
        title="بازگشت"
      >
        <Undo className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="p-1.5 rounded hover:bg-gray-200 transition disabled:opacity-50"
        title="باز انجام"
      >
        <Redo className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive('bold') ? 'bg-gray-200 text-blue-600' : ''}`}
        title="پررنگ"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive('italic') ? 'bg-gray-200 text-blue-600' : ''}`}
        title="ایتالیک"
      >
        <Italic className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition text-xs font-bold ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="عنوان اصلی"
      >
        <Heading1 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition text-xs font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="عنوان فرعی"
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition text-xs font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="عنوان کوچک"
      >
        <Heading3 className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive('bulletList') ? 'bg-gray-200 text-blue-600' : ''}`}
        title="لیست نامرتب"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive('orderedList') ? 'bg-gray-200 text-blue-600' : ''}`}
        title="لیست مرتب"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="چپ‌چین"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="وسط‌چین"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200 text-blue-600' : ''}`}
        title="راست‌چین"
      >
        <AlignRight className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={addLink}
        className="p-1.5 rounded hover:bg-gray-200 transition"
        title="لینک"
      >
        <LinkIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={addImage}
        className="p-1.5 rounded hover:bg-gray-200 transition"
        title="تصویر"
      >
        <ImageIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition ${editor.isActive('codeBlock') ? 'bg-gray-200 text-blue-600' : ''}`}
        title="کد"
      >
        <Code className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============================================================
// کامپوننت اصلی
// ============================================================
export const PackageEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSEOPanel, setShowSEOPanel] = useState(false);
  const [manualSlug, setManualSlug] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    short_description: '',
    description: '',
    power_rating: '',
    total_price: '',
    discount_price: '',
    discount_percent: 0,
    is_featured: false,
    is_active: true,
    items: [],
    show_price_on_call: false,
    contact_phone: '',
  });
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [powerOptions, setPowerOptions] = useState([]);
  const [powerLoading, setPowerLoading] = useState(false);
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
  });

  const isNew = !id || id === 'new';

  // ========== ادیتور TipTap ==========
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image,
      Placeholder.configure({
        placeholder: 'توضیحات پکیج را اینجا بنویسید... از دکمه‌های بالا برای فرمت‌دهی استفاده کنید.',
      }),
    ],
    content: formData.description || '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, description: editor.getHTML() }));
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[200px] p-4',
        dir: 'rtl',
      },
    },
  });

  // وقتی formData.description تغییر کرد، ادیتور را آپدیت کن
  useEffect(() => {
    if (editor && formData.description !== undefined && editor.getHTML() !== formData.description) {
      editor.commands.setContent(formData.description || '');
    }
  }, [editor, formData.description]);

  // ========== دریافت توان‌ها از سرور ==========
  const fetchPowerOptions = async () => {
    setPowerLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/products/power-ratings/'), {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      const data = await response.json();
      setPowerOptions(data.results || data || []);
    } catch (error) {
      console.error('Error fetching power options:', error);
    } finally {
      setPowerLoading(false);
    }
  };

  // ========== بارگذاری اولیه ==========
  useEffect(() => {
    fetchPowerOptions();
    fetchProducts();
    if (!isNew) {
      fetchPackage();
    } else {
      setLoading(false);
    }
  }, [id]);

  // ========== جستجوی محصولات با Debounce ==========
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (productSearch.length >= 2 || productSearch.length === 0) {
        setProductPage(1);
        fetchProducts(productSearch, 1);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [productSearch]);

  const fetchProducts = async (search = '', page = 1) => {
    setProductsLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const params = new URLSearchParams({
        page: page,
        page_size: 20,
        status: 'active',
      });
      if (search) params.append('search', search);

      const response = await fetch(
        config.getApiUrl(`/products/products/?${params.toString()}`),
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );
      const data = await response.json();

      if (page === 1) {
        setProducts(data.results || []);
      } else {
        setProducts(prev => [...prev, ...(data.results || [])]);
      }

      setProductTotalCount(data.count || 0);
      setProductTotalPages(Math.ceil((data.count || 0) / 20));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchPackage = async () => {
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/products/packages/${encodeURIComponent(id)}/`), {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('پکیج یافت نشد');
      }

      const data = await response.json();

      // ✅ پردازش تصاویر برای نمایش
      const processedDescription = processImageUrls(data.description || '');

      setFormData({
        name: data.name || '',
        slug: data.slug || '',
        short_description: data.short_description || '',
        description: processedDescription,
        power_rating: data.power_rating?.id || data.power_rating || '',
        total_price: data.total_price || '',
        discount_price: data.discount_price || '',
        discount_percent: data.discount_percent || 0,
        show_price_on_call: data.show_price_on_call || false,
        contact_phone: data.contact_phone || '',
        is_featured: data.is_featured || false,
        is_active: data.is_active !== undefined ? data.is_active : true,
        items: (data.items || []).map(item => ({
          id: item.id,
          product: item.product,
          product_name: item.product_name,
          product_slug: item.product_slug,
          quantity: item.quantity
        })),
      });
      setManualSlug(true);
      setSeoData({
        meta_title: data.meta_title || data.name,
        meta_description: data.meta_description || data.description?.replace(/<[^>]*>/g, '').substring(0, 160) || '',
        meta_keywords: data.meta_keywords || '',
        focus_keyword: data.focus_keyword || '',
      });
    } catch (error) {
      console.error('Error fetching package:', error);
      alert('خطا در بارگذاری اطلاعات');
      navigate('/admin/packages');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'name') {
      const newName = value;
      if (!manualSlug) {
        const generatedSlug = slugify(newName);
        setFormData(prev => ({ ...prev, name: newName, slug: generatedSlug }));
      } else {
        setFormData(prev => ({ ...prev, name: newName }));
      }
    } else if (name === 'slug') {
      let cleanSlug = value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/_/g, '-')
        .replace(/[^\w\u0600-\u06FF\-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug: cleanSlug }));
      setManualSlug(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addItem = () => {
    if (!selectedProduct) {
      alert('لطفاً محصول را از لیست انتخاب کنید');
      return;
    }

    if (formData.items.some(item => item.product === parseInt(selectedProduct))) {
      alert('این محصول قبلاً به پکیج اضافه شده است');
      return;
    }

    const product = products.find(p => p.id === parseInt(selectedProduct));
    if (!product) {
      alert('محصول یافت نشد');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now(),
        product: product.id,
        product_name: product.name,
        product_slug: product.slug,
        quantity: selectedQuantity
      }]
    }));

    setSelectedProduct('');
    setSelectedQuantity(1);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItemQuantity = (index, quantity) => {
    const newItems = [...formData.items];
    newItems[index].quantity = Math.max(1, parseInt(quantity) || 1);
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleSaveSEO = async (newSeoData) => {
    setSeoData(newSeoData);
    setShowSEOPanel(false);
    alert('تنظیمات سئو ذخیره شد (برای ذخیره نهایی، فرم را ارسال کنید)');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalSlug = formData.slug;
    if (finalSlug) {
      finalSlug = slugify(finalSlug);
    }
    if (!finalSlug) {
      finalSlug = slugify(formData.name);
    }

    if (!finalSlug) {
      alert('آدرس یکتا (slug) نمی‌تواند خالی باشد');
      return;
    }

    if (!formData.show_price_on_call) {
      if (!formData.total_price || parseInt(formData.total_price) <= 0) {
        alert('لطفاً قیمت کل را وارد کنید');
        return;
      }
    }

    if (formData.total_price && parseInt(formData.total_price) < 0) {
      alert('قیمت کل نمی‌تواند منفی باشد');
      return;
    }

    if (formData.discount_price && parseInt(formData.discount_price) < 0) {
      alert('قیمت با تخفیف نمی‌تواند منفی باشد');
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    setSubmitting(true);

    try {
      const itemsToSend = formData.items.map(item => ({
        product: item.product,
        quantity: item.quantity
      }));

      // ✅ استفاده از cleanImageUrls برای پاک‌سازی مسیرها
      const cleanDescription = cleanImageUrls(formData.description);

      const updateData = {
        name: formData.name,
        slug: finalSlug,
        short_description: formData.short_description || '',
        description: cleanDescription,
        power_rating: formData.power_rating || null,
        total_price: formData.show_price_on_call ? null : (formData.total_price ? parseInt(formData.total_price) : null),
        discount_price: formData.discount_price ? parseInt(formData.discount_price) : null,
        discount_percent: parseInt(formData.discount_percent) || 0,
        show_price_on_call: formData.show_price_on_call,
        contact_phone: formData.contact_phone || null,
        is_featured: formData.is_featured,
        is_active: formData.is_active,
        items: itemsToSend,
        meta_title: seoData.meta_title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        focus_keyword: seoData.focus_keyword,
      };

      let url, method;
      if (isNew) {
        url = config.getApiUrl('/products/packages/');
        method = 'POST';
      } else {
        url = config.getApiUrl(`/products/packages/${encodeURIComponent(id)}/`);
        method = 'PUT';
      }

      console.log('isNew:', isNew);
      console.log('Sending request to:', url);
      console.log('Method:', method);

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(updateData)
      });

      const responseData = await response.json();
      console.log('Response:', responseData);

      if (response.ok) {
        alert(isNew ? 'پکیج با موفقیت ایجاد شد' : 'پکیج با موفقیت ویرایش شد');
        navigate('/admin/packages');
      } else {
        let errorMsg = 'خطا در ذخیره پکیج:\n';
        if (typeof responseData === 'object') {
          Object.entries(responseData).forEach(([key, value]) => {
            errorMsg += `${key}: ${JSON.stringify(value)}\n`;
          });
        } else {
          errorMsg += responseData;
        }
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error saving package:', error);
      alert('خطا در ارتباط با سرور: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getKeywordSuggestions = () => {
    const plainText = formData.description?.replace(/<[^>]*>/g, '') || '';
    const words = plainText.split(/\s+/) || [];
    const wordCount = {};
    const stopWords = ['و', 'به', 'از', 'برای', 'در', 'این', 'آن', 'که', 'نیز', 'با', 'را', 'تا'];

    words.forEach(word => {
      const cleanWord = word.replace(/[،،.!؟;:]/g, '').toLowerCase();
      if (cleanWord.length > 3 && !stopWords.includes(cleanWord)) {
        wordCount[cleanWord] = (wordCount[cleanWord] || 0) + 1;
      }
    });
    return Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([word]) => word);
  };

  if (loading || powerLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title={`${isNew ? 'افزودن' : 'ویرایش'} پکیج | پنل مدیریت`} noindex={true} />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {isNew ? 'افزودن پکیج جدید' : 'ویرایش پکیج'}
              </h1>
              <button
                type="button"
                onClick={() => setShowSEOPanel(!showSEOPanel)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Globe className="w-4 h-4" />
                {showSEOPanel ? 'بستن تنظیمات سئو' : 'تنظیمات سئو (پیشرفته)'}
              </button>
            </div>

            {showSEOPanel && (
              <SEOEditor
                initialData={seoData}
                onSave={handleSaveSEO}
                onCancel={() => setShowSEOPanel(false)}
                content={formData.description?.replace(/<[^>]*>/g, '') || ''}
                title="تنظیمات سئوی پکیج"
                keywordsSuggestion={getKeywordSuggestions()}
              />
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام پکیج *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                    {manualSlug && <span className="text-xs text-blue-600 mr-2">(دستی)</span>}
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                    placeholder="مثال: پکیج-خورشیدی-10kw"
                  />
                  <small className="text-xs text-gray-500 block mt-1">
                    آدرس نهایی: {config.SITE_URL}/packages/{formData.slug || '...'}
                  </small>
                  {!manualSlug && (
                    <small className="text-xs text-green-600 block mt-1">
                      💡 با تغییر نام پکیج، این آدرس به‌طور خودکار ساخته می‌شود
                    </small>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">توان پکیج *</label>
                  <select
                    name="power_rating"
                    value={formData.power_rating || ''}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">انتخاب کنید</option>
                    {powerOptions.length > 0 ? (
                      powerOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.label || `${option.value} کیلووات`}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>هیچ توانی تعریف نشده است</option>
                    )}
                  </select>
                  {powerOptions.length === 0 && (
                    <p className="text-xs text-yellow-600 mt-1">
                      ⚠️ هیچ توانی تعریف نشده است. لطفاً ابتدا در پنل ادمین توان‌ها را تعریف کنید.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      قیمت کل (تومان)
                      {!formData.show_price_on_call && <span className="text-red-500"> *</span>}
                      {formData.show_price_on_call && <span className="text-xs text-gray-400 mr-1">(اختیاری)</span>}
                    </label>
                    <input
                      type="number"
                      name="total_price"
                      value={formData.total_price}
                      onChange={handleInputChange}
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${!formData.show_price_on_call ? 'border-red-300' : ''
                        }`}
                      required={!formData.show_price_on_call}
                      placeholder={formData.show_price_on_call ? 'اختیاری' : 'مثال: 10000000'}
                    />
                    {formData.show_price_on_call && (
                      <p className="text-xs text-gray-400 mt-1">در صورت فعال بودن "تماس برای قیمت"، وارد کردن قیمت اختیاری است</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      قیمت با تخفیف (تومان)
                      <span className="text-xs text-gray-400 mr-1">(اختیاری)</span>
                    </label>
                    <input
                      type="number"
                      name="discount_price"
                      value={formData.discount_price}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="اختیاری"
                    />
                  </div>
                </div>
              </div>

              {/* ========== بخش تماس برای قیمت ========== */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">تنظیمات تماس برای قیمت</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="show_price_on_call"
                      checked={formData.show_price_on_call}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <label className="text-sm font-medium text-gray-700 cursor-pointer">
                        نمایش "تماس برای قیمت"
                      </label>
                      <p className="text-xs text-gray-500">
                        در صورت فعال بودن، قیمت پکیج نمایش داده نمی‌شود و دکمه تماس نشان داده می‌شود
                      </p>
                      {formData.show_price_on_call && (
                        <p className="text-xs text-green-600 mt-1">
                          ✅ با فعال شدن این گزینه، وارد کردن قیمت اجباری نیست
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">شماره تماس اختصاصی</label>
                    <input
                      type="text"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${formData.show_price_on_call && !formData.contact_phone ? 'border-yellow-300' : ''
                        }`}
                      placeholder="مثال: 09123456789"
                      disabled={!formData.show_price_on_call}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {formData.show_price_on_call
                        ? 'در صورت خالی بودن، شماره اصلی سایت نمایش داده می‌شود'
                        : 'برای فعال کردن این بخش، گزینه "تماس برای قیمت" را فعال کنید'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ========== خلاصه توضیحات ========== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  خلاصه توضیحات
                  <span className="text-xs text-gray-400 mr-1">(برای نمایش در لیست پکیج‌ها)</span>
                </label>
                <textarea
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="یک خلاصه کوتاه از پکیج (بدون تگ HTML)..."
                />
                <p className="text-xs text-gray-400 mt-1">این متن در لیست پکیج‌ها نمایش داده می‌شود و از تگ‌های HTML پاکسازی می‌شود.</p>
              </div>

              {/* ========== توضیحات با ویرایشگر ========== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">توضیحات پکیج *</label>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <MenuBar editor={editor} />
                  <EditorContent editor={editor} />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">پکیج ویژه</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">فعال</span>
                </label>
              </div>

              {/* محصولات پکیج */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">محصولات داخل پکیج</h3>

                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {formData.items.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      هیچ محصولی به پکیج اضافه نشده است
                    </div>
                  ) : (
                    formData.items.map((item, index) => (
                      <div key={item.id || index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <span className="font-medium">{item.product_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(index, e.target.value)}
                            className="w-20 border rounded-lg px-2 py-1 text-center"
                            min="1"
                          />
                          <span className="text-sm text-gray-500">عدد</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* بخش انتخاب محصول با جستجو */}
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px] relative">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="جستجوی محصول..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        onFocus={() => setShowProductDropdown(true)}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 pr-8"
                      />
                      {productsLoading && (
                        <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                      )}
                    </div>

                    {/* dropdown لیست محصولات */}
                    {showProductDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {productsLoading ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                            در حال بارگذاری...
                          </div>
                        ) : products.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            محصولی یافت نشد
                          </div>
                        ) : (
                          <>
                            {products.map(product => {
                              const isAdded = formData.items.some(item => item.product === product.id);
                              return (
                                <div
                                  key={product.id}
                                  onClick={() => {
                                    if (!isAdded) {
                                      setSelectedProduct(product.id);
                                      const productObj = products.find(p => p.id === product.id);
                                      if (productObj) {
                                        setFormData(prev => ({
                                          ...prev,
                                          items: [...prev.items, {
                                            id: Date.now(),
                                            product: productObj.id,
                                            product_name: productObj.name,
                                            product_slug: productObj.slug,
                                            quantity: 1
                                          }]
                                        }));
                                      }
                                    }
                                    setShowProductDropdown(false);
                                    setProductSearch('');
                                  }}
                                  className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 flex justify-between items-center ${isAdded ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                                    }`}
                                >
                                  <div>
                                    <div className="font-medium text-gray-800">{product.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {product.brand || 'بدون برند'} • {product.price?.toLocaleString()} تومان
                                    </div>
                                  </div>
                                  {isAdded ? (
                                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">اضافه شده</span>
                                  ) : (
                                    <span className="text-xs text-blue-600">+ اضافه</span>
                                  )}
                                </div>
                              );
                            })}

                            {/* دکمه بارگذاری بیشتر */}
                            {productPage < productTotalPages && (
                              <button
                                onClick={() => {
                                  const nextPage = productPage + 1;
                                  setProductPage(nextPage);
                                  fetchProducts(productSearch, nextPage);
                                }}
                                className="w-full p-3 text-center text-blue-600 hover:bg-blue-50 transition"
                              >
                                نمایش محصولات بیشتر ({productTotalCount} محصول موجود)
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* بستن dropdown با کلیک خارج */}
                    {showProductDropdown && (
                      <div
                        className="fixed inset-0 z-0"
                        onClick={() => setShowProductDropdown(false)}
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={selectedQuantity}
                      onChange={(e) => setSelectedQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24 border rounded-lg px-3 py-2 text-center"
                      min="1"
                    />
                    <span className="text-sm text-gray-500">عدد</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedProduct) {
                        alert('لطفاً یک محصول را از لیست انتخاب کنید');
                        return;
                      }
                      addItem();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    افزودن
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  {productTotalCount > 0 && (
                    <span>در مجموع {productTotalCount} محصول فعال موجود است. ({products.length} محصول نمایش داده شده)</span>
                  )}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/admin/packages')}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {submitting ? 'در حال ذخیره...' : 'ذخیره پکیج'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
